import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import MainLayout from '../../components/layouts/MainLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';

import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';

const STATUS_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
];

const FigureDatabaseManagement = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);

  const figureDatabaseController = useMemo(() => new CONTROLLERS.FigureDatabaseController(api), [api]);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  const [characterFilter, setCharacterFilter] = useState('');

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'figure_database:stats',
        params: { v: 1 },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (cached?.stats) {
        setStats(cached.stats);
      }

      const res = await figureDatabaseController.getAdminStats();
      const parsed = res?.data ?? res;
      setStats(parsed);
      await SERVICES.cacheAdminList(cacheKey, { stats: parsed }, { scope: 'figure_database:stats' });
    } catch (e) {
      console.error('Failed to fetch figure database stats:', e);
    } finally {
      setStatsLoading(false);
    }
  }, [figureDatabaseController, auth?.user?.id]);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const offset = page * rowsPerPage;
      const params = {
        limit: rowsPerPage,
        offset,
        status: statusFilter ? [statusFilter] : undefined,
        category: categoryFilter.trim() || undefined,
        origin: originFilter.trim() || undefined,
        character: characterFilter.trim() || undefined,
      };

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'figure_database:list',
        params: { ...params, q: searchQuery.trim() || undefined },
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (Array.isArray(cached?.rows)) {
        setItems(cached.rows);
        setTotalItems(Number.isFinite(cached.totalItems) ? cached.totalItems : 0);
      }

      const res = await figureDatabaseController.getLatestEntries(params);
      const rows = res?.data ?? [];
      const count = res?.count ?? 0;

      const filtered = searchQuery.trim()
        ? (rows || []).filter((r) => {
            const name = String(r?.figure?.name || '').toLowerCase();
            const q = searchQuery.trim().toLowerCase();
            return name.includes(q);
          })
        : rows;

      setItems(Array.isArray(filtered) ? filtered : []);
      setTotalItems(Number.isFinite(count) ? count : 0);

      await SERVICES.cacheAdminList(
        cacheKey,
        { rows: Array.isArray(filtered) ? filtered : [], totalItems: Number.isFinite(count) ? count : 0 },
        { scope: 'figure_database:list' },
      );
    } catch (e) {
      console.error('Failed to fetch figure database entries:', e);
      setErrorMessage(e?.response?.data?.message || e?.message || 'Failed to load figure database entries');
      setItems([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [figureDatabaseController, auth?.user?.id, page, rowsPerPage, searchQuery, statusFilter, categoryFilter, originFilter, characterFilter]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleOpenPreview = async (row) => {
    try {
      const id = row?.id;
      if (!id) return;
      const res = await figureDatabaseController.getEntry(id);
      const data = res?.data?.data ?? res?.data ?? res;
      setSelectedEntry(data);
      setPreviewOpen(true);
    } catch (e) {
      console.error('Failed to fetch entry:', e);
      showSnackbar('Failed to load entry', 'error');
    }
  };

  const handleOpenDelete = (row) => {
    setSelectedEntry(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (!selectedEntry?.id) return;
      await figureDatabaseController.deleteEntry(selectedEntry.id);
      showSnackbar('Entry deleted', 'success');
      setDeleteOpen(false);
      fetchEntries();
      fetchStats();
    } catch (e) {
      console.error('Delete entry failed:', e);
      showSnackbar(e?.response?.data?.message || e?.message || 'Failed to delete entry', 'error');
    }
  };

  const getStatusColor = (status) => {
    const v = String(status || '').toLowerCase();
    if (v === 'approved') return 'success';
    if (v === 'pending') return 'warning';
    if (v === 'rejected') return 'error';
    return 'default';
  };

  const statsCards = [
    { label: 'Total', value: stats?.total ?? 0 },
    { label: 'Approved', value: stats?.approved ?? 0 },
    { label: 'Pending', value: stats?.pending ?? 0 },
    { label: 'Rejected', value: stats?.rejected ?? 0 },
  ];

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>Figure Database Management</Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Manage figure database entries
        </Typography>

        <Grid container spacing={2} mb={3}>
          {statsLoading && !stats ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Grid>
          ) : (
            statsCards.map((c) => (
              <Grid item xs={12} sm={6} md={3} key={c.label}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h6" fontWeight={800}>{Number(c.value).toLocaleString()}</Typography>
                        <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: 'background.paper' }}>
                        <StorageIcon color="action" />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 220px 1fr 1fr 1fr', gap: 2 }}>
              <TextField
                label="Search (name)"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                size="small"
              />

              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Category"
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
                size="small"
              />

              <TextField
                label="Origin"
                value={originFilter}
                onChange={(e) => { setOriginFilter(e.target.value); setPage(0); }}
                size="small"
              />

              <TextField
                label="Character"
                value={characterFilter}
                onChange={(e) => { setCharacterFilter(e.target.value); setPage(0); }}
                size="small"
              />
            </Box>

            {errorMessage ? (
              <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>
            ) : null}

            <TableContainer component={Paper} sx={{ boxShadow: 'none', mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entry</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Submitted By</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">No entries found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              src={row?.figure?.figureImages?.[0] || row?.figure?.imageUrl || undefined}
                              sx={{ width: 36, height: 36 }}
                              variant="rounded"
                            >
                              <StorageIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={800} noWrap sx={{ maxWidth: 420 }}>
                                {row?.figure?.name || 'Untitled'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {row?.figure?.origin || '—'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={row.status} size="small" color={getStatusColor(row.status)} sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row?.submittedBy?.username || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row?.companyName || row?.figure?.company || '—'}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenPreview(row)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleOpenDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[12, 24, 48]}
            />
          </CardContent>
        </Card>

        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Entry Preview</DialogTitle>
          <DialogContent>
            {selectedEntry ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 2 }}>
                <Avatar
                  src={selectedEntry?.figure?.figureImages?.[0] || selectedEntry?.figure?.imageUrl || undefined}
                  variant="rounded"
                  sx={{ width: 160, height: 160 }}
                />
                <Box>
                  <Typography variant="h6" fontWeight={900}>
                    {selectedEntry?.figure?.name || 'Untitled'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedEntry?.figure?.description || '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                    {selectedEntry?.figure?.origin ? <Chip label={`Origin: ${selectedEntry.figure.origin}`} size="small" /> : null}
                    {selectedEntry?.figure?.character ? <Chip label={`Character: ${selectedEntry.figure.character}`} size="small" /> : null}
                    {selectedEntry?.figure?.category ? <Chip label={`Category: ${selectedEntry.figure.category}`} size="small" /> : null}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Entry</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              This will delete the figure database entry.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>Delete</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default FigureDatabaseManagement;
